import {
  useSyncExternalStore,
} from 'react';

import {
  createCanonicalTreeTabScopedSanitizedEvidenceRegistry,
  getCanonicalTreeTabScopedSanitizedEvidenceRegistry,
  type CanonicalTreeTabScopedEvidenceRegistry,
  type CanonicalTreeTabScopedEvidenceRegistrySnapshot,
  type CanonicalTreeTabScopedEvidenceRegistryUnsubscribe,
} from '@/governor/tree/CanonicalTreeTabScopedSanitizedEvidenceRegistry';

export type CanonicalTreeRegistryEvidenceReactSnapshot =
  CanonicalTreeTabScopedEvidenceRegistrySnapshot;

export type CanonicalTreeRegistryEvidenceReactListener =
  () => void;

export interface CanonicalTreeRegistryEvidenceReactStore {
  readonly getSnapshot:
    () => CanonicalTreeRegistryEvidenceReactSnapshot;

  readonly getServerSnapshot:
    () => CanonicalTreeRegistryEvidenceReactSnapshot;

  readonly subscribe: (
    listener:
      CanonicalTreeRegistryEvidenceReactListener,
  ) => CanonicalTreeTabScopedEvidenceRegistryUnsubscribe;
}

export const CANONICAL_TREE_REGISTRY_SERVER_SNAPSHOT:
  CanonicalTreeRegistryEvidenceReactSnapshot =
  createCanonicalTreeTabScopedSanitizedEvidenceRegistry()
    .getSnapshot();

const safelyReadRegistrySnapshot = (
  registry:
    CanonicalTreeTabScopedEvidenceRegistry,

  fallback:
    CanonicalTreeRegistryEvidenceReactSnapshot,
): CanonicalTreeRegistryEvidenceReactSnapshot => {
  try {
    return registry.getSnapshot();
  } catch {
    return fallback;
  }
};

export const createCanonicalTreeRegistryEvidenceReactStore =
  (
    registry:
      CanonicalTreeTabScopedEvidenceRegistry,

    serverSnapshot:
      CanonicalTreeRegistryEvidenceReactSnapshot =
      CANONICAL_TREE_REGISTRY_SERVER_SNAPSHOT,
  ): CanonicalTreeRegistryEvidenceReactStore => {
    const getSnapshot =
      (): CanonicalTreeRegistryEvidenceReactSnapshot =>
        safelyReadRegistrySnapshot(
          registry,
          serverSnapshot,
        );

    return Object.freeze({
      getSnapshot,

      getServerSnapshot:
        () => serverSnapshot,

      subscribe: (
        listener:
          CanonicalTreeRegistryEvidenceReactListener,
      ): CanonicalTreeTabScopedEvidenceRegistryUnsubscribe => {
        let active = true;

        let registryUnsubscribe:
          CanonicalTreeTabScopedEvidenceRegistryUnsubscribe =
          () => {};

        try {
          registryUnsubscribe =
            registry.subscribe(() => {
              if (!active) {
                return;
              }

              try {
                listener();
              } catch {
                // Consumer render notification failure is non-fatal.
              }
            });
        } catch {
          active = false;
        }

        return () => {
          if (!active) {
            return;
          }

          active = false;

          const unsubscribe =
            registryUnsubscribe;

          registryUnsubscribe =
            () => {};

          try {
            unsubscribe();
          } catch {
            // Registry subscription disposal failure is non-fatal.
          }
        };
      },
    });
  };

let defaultRegistryReactStore:
  CanonicalTreeRegistryEvidenceReactStore |
  null =
  null;

const getDefaultRegistryReactStore =
  (): CanonicalTreeRegistryEvidenceReactStore => {
    if (!defaultRegistryReactStore) {
      defaultRegistryReactStore =
        createCanonicalTreeRegistryEvidenceReactStore(
          getCanonicalTreeTabScopedSanitizedEvidenceRegistry(),
        );
    }

    return defaultRegistryReactStore;
  };

export const useCanonicalTreeRegistryEvidence =
  (): CanonicalTreeRegistryEvidenceReactSnapshot => {
    const store =
      getDefaultRegistryReactStore();

    return useSyncExternalStore(
      store.subscribe,
      store.getSnapshot,
      store.getServerSnapshot,
    );
  };
